import React from "react";
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Select,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  VStack,
  useColorModeValue,
} from "@chakra-ui/react";
import { useQuery } from "@tanstack/react-query";
import goalsApi from "../api/goalsApi";
import ModulePageShell from "../components/common/ModulePageShell";
import EmptyState from "../components/common/EmptyState";
import LoadingState from "../components/common/LoadingState";
import { PERFORMANCE_QUERY_KEYS } from "../constants/performanceConstants";
import usePerformancePermissions from "../hooks/usePerformancePermissions";
import useGoalOptions from "../hooks/useGoalOptions";

const GoalCheckinsPage = () => {
  const { can, PERFORMANCE_PERMISSIONS } = usePerformancePermissions();
  const [showGuide, setShowGuide] = React.useState(false);
  const { goalOptions, isLoading: goalsLoading } = useGoalOptions();
  const [selectedGoalId, setSelectedGoalId] = React.useState("");
  const cardBg = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.700");

  const goalQuery = useQuery({
    queryKey: [PERFORMANCE_QUERY_KEYS.goalDetail, selectedGoalId],
    queryFn: () => goalsApi.getGoalById(selectedGoalId),
    enabled: Boolean(selectedGoalId),
  });

  const checkins = goalQuery.data?.data?.checkins || [];

  if (!can(PERFORMANCE_PERMISSIONS.GOALS_READ)) {
    return (
      <ModulePageShell
        title="Goal Check-ins"
        description="Search by goal ID to view the complete check-in timeline."
      >
        <EmptyState message="You do not have permission to view goal check-ins." />
      </ModulePageShell>
    );
  }

  return (
    <ModulePageShell
      title="Goal Check-ins"
      description="Search by goal ID to view the complete check-in timeline."
      onRefresh={() => goalQuery.refetch()}
      showGuide={showGuide}
      onToggleGuide={() => setShowGuide((prev) => !prev)}
    >
      <Box
        bg={cardBg}
        borderWidth="1px"
        borderColor={borderColor}
        borderRadius="lg"
        p={4}
      >
        <VStack align="stretch" spacing={3}>
          <FormControl>
            <FormLabel>Goal ID</FormLabel>
            <Select
              placeholder="Select goal"
              value={selectedGoalId}
              onChange={(e) => setSelectedGoalId(e.target.value)}
              isDisabled={goalsLoading}
            >
              {goalOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Select>
          </FormControl>
          <Button
            alignSelf="flex-start"
            onClick={() => setSelectedGoalId(selectedGoalId.trim())}
            colorScheme="blue"
            isDisabled={!selectedGoalId.trim()}
          >
            Load Check-ins
          </Button>
        </VStack>
      </Box>

      {goalQuery.isLoading ? (
        <LoadingState />
      ) : !selectedGoalId ? (
        <EmptyState message="Select a goal to view check-ins." />
      ) : checkins.length === 0 ? (
        <EmptyState message="No check-ins recorded for this goal yet." />
      ) : (
        <Box
          bg={cardBg}
          borderWidth="1px"
          borderColor={borderColor}
          borderRadius="lg"
          p={4}
        >
          <Text fontWeight="semibold" mb={3}>
            Timeline
          </Text>
          <Table size="sm">
            <Thead>
              <Tr>
                <Th>Date</Th>
                <Th>Progress</Th>
                <Th>Comment</Th>
                <Th>Next Steps</Th>
              </Tr>
            </Thead>
            <Tbody>
              {checkins.map((item) => (
                <Tr key={item._id}>
                  <Td>
                    {item.createdAt
                      ? new Date(item.createdAt).toLocaleDateString()
                      : "-"}
                  </Td>
                  <Td>{item.progressPercentage || 0}%</Td>
                  <Td>{item.updateText || "-"}</Td>
                  <Td>{item.nextSteps || "-"}</Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </Box>
      )}
    </ModulePageShell>
  );
};

export default GoalCheckinsPage;
